import React, { useEffect, useState } from "react";
import {
  Button,
  TextField,
  Grid,
  Typography,
  Box,
  Container,
  Paper,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  IconButton,
  FormControlLabel,
  Checkbox,
  Switch,
  SelectChangeEvent,
} from "@mui/material";
import { Formik, Form, Field, getIn, FieldArray } from "formik";
import * as Yup from "yup";
import {
  ContractAdditional,
  ContractFixedBilling,
  ContractFormData,
  ContractFuelConfig,
  ContractProfitSharing,
  CreateContractRequest,
  CreateSuperProjectRequest,
  FetchedContract,
  ProjectModule,
} from "../types";
import {
  commonTextFieldProps,
  commonDateFieldProps,
  commonButtonProps,
  smallButtonProp,
} from "../../../utils/styles";
import SearchableAutocomplete from "../../../components/SearchableAutocomplete";
import { Client } from "../../../pages/masterData/client/types";
import { formatText } from "../../../utils/utils";
import NavigationHeader from "../../../components/NavigationHeader";
import { PROJECT_TERMINOLOGY, ProjectTabEnum } from "../enums";
import { Add, Delete } from "@mui/icons-material";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import {
  AdditionalType,
  BillingCycle,
  Category,
  ContractStatus,
  ContractType,
  getVehicleMakes,
  getVehicleModels,
  PricingType,
  ProfitCalculationMethod,
  RateType,
  ResourceType,
  VehicleCategory,
} from "../enums/contractEnums";
import { Autocomplete } from "@mui/material";
import { formatLaborType, getAvailableContractTypes } from "./ContractForm";
import { DocumentUploadSection } from "../../../components/DocumentUploadSection";
import { superProjectApi } from "../services/apiService";
import { Loading } from "../../../components/Loading";
import { useToast } from "../../../components/Toast";
import { generateContractNumber } from "./ContractForm";
import { uploadFile } from "../../../utils/UploadFile";

const validationSchema = Yup.object({
  name: Yup.string()
    .required(`${PROJECT_TERMINOLOGY.SINGULAR} name is required`)
    .min(3, "Name must be at least 3 characters"),
  description: Yup.string(),
  startDate: Yup.date().required("Start date is required"),
  endDate: Yup.date().min(
    Yup.ref("startDate"),
    "End date must be after start date"
  ),
  clientId: Yup.string().required("Client is required"),
  contract: Yup.object(),
  onDivisionBasis: Yup.boolean(),
  divisions: Yup.array().when("onDivisionBasis", {
    is: true,
    then: (schema) =>
      schema.of(
        Yup.object().shape({
          name: Yup.string().required("Division name is required"),
          description: Yup.string(),
        })
      ),
    otherwise: (schema) =>
      schema.of(
        Yup.object().shape({
          name: Yup.string().optional(),
          description: Yup.string(),
        })
      ),
  }),
});

const SuperProjectForm: React.FC = () => {
  const navigate = useNavigate();
  const { showToast } = useToast();
  const { id } = useParams<{ id: string }>();
  const isEditMode = !!id;
  const [superProject, setSuperProject] = useState<any>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [selectedFiles, setSelectedFiles] = useState<File[]>([]);

  useEffect(() => {
    if (isEditMode) {
      fetchSuperProject();
    }
  }, [isEditMode, id]);

  const fetchSuperProject = async () => {
    if (!id) return;
    try {
      setIsLoading(true);
      const data = await superProjectApi.getSuperProject(id as string);
      setSuperProject(data);
    } catch (error) {
      console.error("Error fetching super project:", error);
    } finally {
      setIsLoading(false);
    }
  };

  const getContractInitialValues = (): ContractFormData => {
    if (isEditMode && superProject) {
      // Return converted contract data for edit mode
      return convertContractToFormData(
        superProject?.contract as FetchedContract
      );
    }

    // Return default values for create mode
    return {
      basicInfo: {
        contractType: ContractType.VEHICLE_ONLY,
        category: Category.SELECT_CATEGORY,
        billingCycle: BillingCycle.MONTHLY,
        status: ContractStatus.DRAFT,
        vehicleCount: undefined,
        resourceCount: undefined,
      },
      profitSharing: {
        profitSharingEnabled: false,
        profitCalculationMethod: ProfitCalculationMethod.NET_PROFIT,
      },
      fuelConfig: {
        fuelIncluded: false,
        fuelRate: 0,
        fuelCity: "",
        fuelVariance: 0,
        fuelMileage: 0,
      },
      // Initialize with one resourceTerms for VEHICLE_INCL_DRIVER
      resourceTerms: [
        {
          resourceType: "DRIVER" as any,
          quantity: 1,
          ratePerResource: "",
          rateType: RateType.DAILY,
        },
      ],
      // Initialize fixedTerms contract terms only for FIXED_BILLING category
      fixedTerms: {
        billingCycle: BillingCycle.MONTHLY,
        minGuaranteeAmount: 0,
        distance: 0,
      },
      // fixedBillingTerms: {
      //   billingCycle: BillingCycle.MONTHLY,
      //   fixedBillingTermItems: [],
      // },
    };
  };

  // Convert contract to form data for editing
  const convertContractToFormData = (
    contract: FetchedContract
  ): ContractFormData => {
    return {
      basicInfo: {
        id: contract.id,
        contractNumber: contract.contractNumber,
        contractType: contract.contractType,
        category: contract.category,
        billingCycle: contract.billingCycle,
        status: contract.status,
        startDate: contract.startDate,
        endDate: contract.endDate || "",
        notes: contract.notes || "",
        vehicleCount: contract.vehicleCount || undefined,
        resourceCount: contract.resourceCount || undefined,
      },
      perKmTerms: contract.perKmTerms?.[0]
        ? {
            kmLimit: contract.perKmTerms?.[0].kmLimit,
            limitType: contract.perKmTerms?.[0].limitType,
            ratePerKm: contract.perKmTerms?.[0].ratePerKm,
            overlimitRate: contract.perKmTerms?.[0].overlimitRate,
          }
        : undefined,
      perSeatTerms: contract.perSeatTerms?.[0]
        ? {
            seatCount: contract.perSeatTerms?.[0].seatCount,
            ratePerSeat: contract.perSeatTerms?.[0].ratePerSeat,
            minSeatsGuarantee: contract.perSeatTerms?.[0].minSeatsGuarantee,
            occupancyThreshold: contract.perSeatTerms?.[0].occupancyThreshold,
          }
        : undefined,
      resourceTerms:
        contract.resourceTerms?.map((term) => ({
          resourceType: term.resourceType,
          quantity: term.quantity,
          ratePerResource: term.ratePerResource,
          rateType: term.rateType,
        })) || undefined,
      fixedTerms: contract.fixedTerms?.[0]
        ? {
            billingCycle: contract.fixedTerms?.[0].billingCycle,
            minGuaranteeAmount: contract.fixedTerms?.[0].minGuaranteeAmount,
            distance: contract.fixedTerms?.[0].distance,
          }
        : undefined,
      profitSharing: contract.profitSharing
        ? {
            profitSharingEnabled: contract.profitSharing.profitSharingEnabled,
            profitSharingPercentage:
              contract.profitSharing?.profitSharingPercentage,
            profitSharingThreshold:
              contract.profitSharing?.profitSharingThreshold,
            profitCalculationMethod:
              contract.profitSharing?.profitCalculationMethod,
          }
        : undefined,
      fuelConfig: contract.fuelConfig
        ? {
            fuelIncluded: contract.fuelConfig?.fuelIncluded,
            fuelRate: contract.fuelConfig?.fuelRate,
            fuelCity: contract.fuelConfig?.fuelCity,
            fuelVariance: contract.fuelConfig?.fuelVariance,
            fuelMileage: contract.fuelConfig?.fuelMileage,
          }
        : undefined,
      additionalTerms:
        contract.additionalTerms?.map((term: ContractAdditional) => ({
          type: term.type,
          amount: term.amount,
          remarks: term.remarks,
        })) || [],
      // fixedBillingTerms: contract.fixedBillingTerms
      //   ? {
      //       billingCycle: contract.fixedBillingTerms?.[0]?.billingCycle,
      //       fixedBillingTermItems:
      //         contract.fixedBillingTerms?.[0]?.fixedBillingTermItems,
      //     }
      //   : undefined,
    };
  };

  const initialValues: CreateSuperProjectRequest = {
    name: superProject?.name || "",
    description: superProject?.description || "",
    startDate: superProject?.startDate
      ? new Date(superProject.startDate).toISOString().split("T")[0]
      : new Date().toISOString().split("T")[0],
    durationInMonths:
      superProject?.startDate && superProject?.endDate
        ? (
            (new Date(superProject.endDate).getTime() -
              new Date(superProject.startDate).getTime()) /
            (1000 * 60 * 60 * 24 * 30)
          ).toFixed(0)
        : "",
    endDate: superProject?.endDate || "",
    clientId: superProject?.clientId || "",
    client: superProject?.client || undefined,
    contract: getContractInitialValues(),
    documents: [],
    onDivisionBasis: superProject?.divisions?.length > 0,
    divisions: superProject?.divisions || [],
  };

  // Helper function to safely access nested errors
  const getFieldError = (fieldPath: string, touched: any, errors: any) => {
    const touchedValue = fieldPath
      .split(".")
      .reduce((obj, key) => obj?.[key], touched);
    const errorValue = fieldPath
      .split(".")
      .reduce((obj, key) => obj?.[key], errors);
    return {
      hasError: touchedValue && errorValue,
      errorMessage: errorValue,
    };
  };

  // Wrapper function to adapt values structure for renderVehicleTerms
  const renderVehicleTermsWrapper = (
    contractValues: any,
    setFieldValue: (field: string, value: any) => void,
    handleChange: (e: React.ChangeEvent<any>) => void,
    touched: any,
    errors: any
  ) => {
    // Create a wrapper setFieldValue that adds 'contract.' prefix
    const adaptedSetFieldValue = (field: string, value: any) => {
      setFieldValue(`contract.${field}`, value);
    };

    // Create a wrapper handleChange that adds 'contract.' prefix to field names
    const adaptedHandleChange = (e: React.ChangeEvent<any>) => {
      const { name, value } = e.target;
      setFieldValue(`contract.${name}`, value);
    };

    return renderVehicleTerms(
      contractValues,
      adaptedSetFieldValue,
      adaptedHandleChange,
      touched,
      errors
    );
  };

  const handleSubmit = async (values: CreateSuperProjectRequest) => {
    try {
      setIsSubmitting(true);
      if (isEditMode) {
        const updateData = {
          id: values.contract?.basicInfo?.id,
          contractNumber: values.contract?.basicInfo?.contractNumber || "",
          contractType: values.contract?.basicInfo?.contractType,
          category: values.contract?.basicInfo?.category,
          billingCycle: values.contract?.basicInfo?.billingCycle,
          status: values.contract?.basicInfo?.status,
          vehicleCount: values.contract?.basicInfo?.vehicleCount,
          resourceCount: values.contract?.basicInfo?.resourceCount,
          startDate: values.contract?.basicInfo?.startDate,
          endDate: values.contract?.basicInfo?.endDate,
          notes: values.contract?.basicInfo?.notes,
          perKmTerms: values.contract?.perKmTerms,
          perSeatTerms: values.contract?.perSeatTerms,
          resourceTerms: values.contract?.resourceTerms,
          fixedTerms: values.contract?.fixedTerms,
          additionalTerms: values.contract?.additionalTerms,
          profitSharing: values.contract?.profitSharing,
          fuelConfig: values.contract?.fuelConfig,
          // fixedBillingTerms: values.contract
          //   ?.fixedBillingTerms as ContractFixedBilling,
          revisionDescription: `${ProjectTabEnum.CONTRACT.label} updated`,
        };
        const updateProjectData = {
          ...values,
          id: id,
          contractId: values.contract?.basicInfo?.id,
          contract: updateData,
        };
        const updatedProject = await superProjectApi.updateSuperProject(
          id as string,
          updateProjectData
        );
        if (updatedProject) {
          if (selectedFiles && selectedFiles?.length > 0) {
            const uploadPromises = selectedFiles.map((file: File) =>
              uploadFile({
                file: file,
                documentType: "project-contract",
                documentTypeId: updatedProject?.contract?.id || "",
                documentContext: "project",
                description: "Project contract documents",
              })
            );
            const results = await Promise.all(uploadPromises);
          }
          showToast(
            `${PROJECT_TERMINOLOGY.SINGULAR} updated successfully`,
            "success"
          );
        }
      } else {
        // Create new contract - only send relevant data based on contract type and category
        if (!values.contract?.basicInfo) {
          return;
        }
        const { contractType, category } = values.contract?.basicInfo;

        const createData: CreateContractRequest = {
          projectId: id as string,
          contractNumber: generateContractNumber(values?.name || ""),
          contractType: values.contract?.basicInfo?.contractType,
          category: values.contract?.basicInfo?.category,
          billingCycle: values.contract?.basicInfo?.billingCycle,
          vehicleCount: values.contract?.basicInfo?.vehicleCount,
          resourceCount: values.contract?.basicInfo?.resourceCount,
          startDate: values.contract?.basicInfo?.startDate || "",
          endDate: values.contract?.basicInfo?.endDate,
          notes: values.contract?.basicInfo?.notes,
          profitSharing: values.contract
            ?.profitSharing as ContractProfitSharing,
          fuelConfig: values.contract?.fuelConfig as ContractFuelConfig,
        };

        // Only include relevant terms based on contract type and category
        if (
          contractType === ContractType.VEHICLE_ONLY ||
          contractType === ContractType.VEHICLE_INCL_LABOR
        ) {
          if (category === Category.PER_KM && values.contract?.perKmTerms) {
            createData.perKmTerms = values.contract?.perKmTerms;
          }
          if (category === Category.PER_SEAT && values.contract?.perSeatTerms) {
            createData.perSeatTerms = values.contract?.perSeatTerms;
          }
          if (category === Category.FIXED && values.contract?.fixedTerms) {
            createData.fixedTerms = values.contract?.fixedTerms;
          }
        }

        // Include resource terms for VEHICLE_INCL_DRIVER and LABOR_ONLY
        if (
          (contractType === ContractType.VEHICLE_INCL_LABOR ||
            contractType === ContractType.LABOR_ONLY) &&
          values.contract?.resourceTerms
        ) {
          createData.resourceTerms = values.contract?.resourceTerms;
        }

        // Include additional terms if any
        if (
          values.contract?.additionalTerms &&
          values.contract?.additionalTerms.length > 0
        ) {
          createData.additionalTerms = values.contract?.additionalTerms;
        }

        // Include fixed billing terms if contract type is vehicle only or vehicle include labor and category is fixed billing
        if (
          (contractType === ContractType.VEHICLE_ONLY ||
            contractType === ContractType.VEHICLE_INCL_LABOR) &&
          category === Category.FIXED_BILLING &&
          values.contract?.fixedBillingTerms
        ) {
          // createData.fixedBillingTerms = values.contract
          //   ?.fixedBillingTerms as ContractFixedBilling;
        }
        const createProjectData = {
          ...values,
          contract: createData,
        };
        const createdProject = await superProjectApi.createSuperProject(
          createProjectData
        );
        if (createdProject) {
          if (selectedFiles && selectedFiles?.length > 0) {
            const uploadPromises = selectedFiles.map((file: File) =>
              uploadFile({
                file: file,
                documentType: "project-contract",
                documentTypeId: createdProject?.contract?.id || "",
                documentContext: "project",
                description: "Project contract documents",
              })
            );
            const results = await Promise.all(uploadPromises);
          }
          showToast(
            `${PROJECT_TERMINOLOGY.SINGULAR} created successfully`,
            "success"
          );
        }
      }
      navigate(`/super`);
    } catch (error) {
      showToast(
        `Failed to save ${PROJECT_TERMINOLOGY.LOWER_SINGULAR}`,
        "error"
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  if (isLoading) {
    return <Loading />;
  }

  return (
    <Container maxWidth="lg" sx={{ mt: 2, mb: 2 }}>
      <NavigationHeader
        title={`Super ${PROJECT_TERMINOLOGY.SINGULAR}`}
        breadcrumbs={[
          {
            label: `${PROJECT_TERMINOLOGY.LOWER_PLURAL}`,
            path: "/super-contracts",
          },
          {
            label: isEditMode
              ? `Edit ${PROJECT_TERMINOLOGY.SINGULAR}`
              : `Create ${PROJECT_TERMINOLOGY.SINGULAR}`,
          },
        ]}
      />
      <Paper sx={{ p: 2 }}>
        <Formik
          initialValues={initialValues}
          validationSchema={validationSchema}
          onSubmit={handleSubmit}
          enableReinitialize
        >
          {({
            values,
            errors,
            touched,
            handleChange,
            handleBlur,
            setFieldValue,
          }) => (
            <Form>
              <Grid container spacing={1}>
                <Grid item xs={12}>
                  <Typography
                    variant="h6"
                    sx={{ fontSize: "0.9rem", fontWeight: 500 }}
                    color="primary.dark"
                  >
                    {`${PROJECT_TERMINOLOGY.SINGULAR} Details`}
                  </Typography>
                </Grid>
                <Grid item xs={3}>
                  <TextField
                    fullWidth
                    name="name"
                    label={`${PROJECT_TERMINOLOGY.SINGULAR} Name`}
                    value={values.name}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    error={touched.name && !!errors.name}
                    helperText={touched.name && errors.name}
                    {...commonTextFieldProps}
                  />
                </Grid>
                {!isEditMode && (
                  <Grid item xs={3}>
                    <SearchableAutocomplete
                      entityType="CLIENT"
                      value={values.client || null}
                      onSelect={(option) => {
                        const client = option as Client;
                        setFieldValue("clientId", client.id);
                        setFieldValue("client", client);
                      }}
                      onChange={(value) => {}}
                      label="Client"
                      placeholder="Search or enter manually..."
                      getOptionLabel={(option) =>
                        (option as Client).clientName || ""
                      }
                      renderOptionContent={(option) => option.clientName || ""}
                      noOptionsText="No options found"
                      disabled={isSubmitting}
                      size="small"
                    />
                    {touched.clientId && errors.clientId && (
                      <Typography
                        variant="caption"
                        color="error"
                        sx={{ mt: 0.5, display: "block" }}
                      >
                        {errors.clientId}
                      </Typography>
                    )}
                  </Grid>
                )}
                <Grid item xs={12} sm={2}>
                  <TextField
                    fullWidth
                    type="date"
                    name="startDate"
                    label="Start Date"
                    value={values.startDate}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    error={touched.startDate && !!errors.startDate}
                    helperText={touched.startDate && errors.startDate}
                    disabled={isEditMode}
                    {...commonDateFieldProps}
                  />
                </Grid>

                <Grid item xs={12} md={2}>
                  <Field
                    as={TextField}
                    name={`durationInMonths`}
                    label="Duration (Months)"
                    type="number"
                    fullWidth
                    placeholder="e.g., 6"
                    inputProps={{
                      min:
                        isEditMode &&
                        superProject &&
                        "startDate" in superProject &&
                        "endDate" in superProject &&
                        superProject.startDate &&
                        superProject.endDate
                          ? Math.min(
                              Math.ceil(
                                (new Date(superProject.endDate).getTime() -
                                  new Date(superProject.startDate).getTime()) /
                                  (1000 * 60 * 60 * 24 * 30)
                              ),
                              Number(initialValues.durationInMonths)
                            )
                          : 1,
                      max: 60,
                      onInvalid: (e: React.FormEvent<HTMLInputElement>) => {
                        (e.target as HTMLInputElement).setCustomValidity(
                          "Duration cannot be decreased."
                        );
                      },
                      onInput: (e: React.FormEvent<HTMLInputElement>) => {
                        (e.target as HTMLInputElement).setCustomValidity("");
                      },
                    }}
                    error={
                      getIn(touched, `durationInMonths`) &&
                      Boolean(getIn(errors, `durationInMonths`))
                    }
                    helperText={
                      getIn(touched, `durationInMonths`) &&
                      getIn(errors, `durationInMonths`)
                    }
                    disabled={isSubmitting}
                    onChange={(e: any) => {
                      const durationStr = e.target.value;
                      const duration = parseFloat(durationStr);
                      setFieldValue(`durationInMonths`, durationStr);

                      // Auto-calculate end date
                      const startDate = values.startDate;
                      if (startDate && !isNaN(duration) && duration > 0) {
                        const start = new Date(startDate);
                        const end = new Date(start);
                        end.setMonth(start.getMonth() + duration);
                        setFieldValue(
                          `endDate`,
                          end.toISOString().split("T")[0]
                        );
                      }
                    }}
                    {...commonTextFieldProps}
                  />
                </Grid>

                {/* Display selected client information */}
                {(values.client || superProject?.clientId) && (
                  <Grid item xs={12}>
                    <Box
                      sx={{
                        p: 1.5,
                        borderRadius: 1,
                        border: "1px solid",
                        borderColor: "primary.light",
                        bgcolor: "#F0F7FF",
                      }}
                    >
                      <Typography
                        variant="subtitle2"
                        color="primary.dark"
                        sx={{ fontSize: "0.75rem" }}
                        gutterBottom
                      >
                        Client Details (From Master Data)
                      </Typography>
                      <Grid container spacing={1}>
                        {Object.entries(
                          (values.client || superProject?.client) as Client
                        ).map(([key, value]) => {
                          if (key === "id" || key === "companyId") return null;
                          return (
                            <Grid item xs={12} sm={6} md={3} key={key}>
                              <Typography
                                variant="body2"
                                color="text.secondary"
                                sx={{ fontSize: "0.75rem" }}
                              >
                                {formatText(key)}: {value}
                              </Typography>
                            </Grid>
                          );
                        })}
                      </Grid>
                    </Box>
                  </Grid>
                )}

                <Grid item xs={12}>
                  <Typography
                    variant="h6"
                    sx={{ fontSize: "0.9rem", fontWeight: 500 }}
                    color="primary.dark"
                  >
                    {`${ProjectTabEnum.CONTRACT.label} Details`}
                  </Typography>
                </Grid>

                {/* Basic Info Section */}
                <Grid item xs={12}>
                  <Typography
                    variant="h6"
                    gutterBottom
                    sx={{ fontSize: "0.75rem", fontWeight: 600 }}
                  >
                    Basic Information
                  </Typography>
                </Grid>
                <Grid container item xs={12} spacing={1}>
                  {/* <Grid item xs={12} md={2}>
                    <TextField
                      {...commonTextFieldProps}
                      fullWidth
                      label={`${ProjectTabEnum.CONTRACT.label} Number`}
                      name="contract.basicInfo.contractNumber"
                      value={values.contract?.basicInfo?.contractNumber}
                      onChange={handleChange}
                      error={
                        getFieldError(
                          `contract.basicInfo.contractNumber`,
                          touched,
                          errors
                        ).hasError
                      }
                      helperText={
                        getFieldError(
                          `contract.basicInfo.contractNumber`,
                          touched,
                          errors
                        ).errorMessage
                      }
                      required
                    />
                  </Grid> */}
                  <Grid item xs={12} md={2}>
                    <FormControl fullWidth {...commonTextFieldProps}>
                      <InputLabel
                        sx={{ fontSize: "0.75rem" }}
                        size="small"
                        required
                      >
                        {ProjectTabEnum.CONTRACT.label} Type
                      </InputLabel>
                      <Select
                        name="contract.basicInfo.contractType"
                        value={values.contract?.basicInfo?.contractType}
                        onChange={(e) => {
                          const newContractType = e.target
                            .value as ContractType;
                          setFieldValue(
                            "contract.basicInfo.contractType",
                            newContractType
                          );

                          // Auto-set contract type when main contract type changes
                          let newCategory =
                            values.contract?.basicInfo?.category;
                          const availableTypes =
                            getAvailableContractTypes(newContractType);
                          if (
                            !availableTypes.includes(newCategory as Category)
                          ) {
                            newCategory = Category.FIXED_BILLING;
                          }
                          setFieldValue(
                            "contract.basicInfo.category",
                            newCategory
                          );

                          // Handle resourceTerms array based on contract type
                          if (newContractType === ContractType.LABOR_ONLY) {
                            setFieldValue("contract.resourceTerms", [
                              {
                                resourceType: "DRIVER" as any,
                                quantity: 1,
                                ratePerResource: "",
                                rateType: RateType.DAILY,
                              },
                            ]);
                          } else if (
                            newContractType === ContractType.VEHICLE_ONLY
                          ) {
                            setFieldValue("contract.resourceTerms", []);
                          } else if (
                            newContractType === ContractType.VEHICLE_INCL_LABOR
                          ) {
                            if (
                              !values.contract?.resourceTerms ||
                              values.contract?.resourceTerms.length === 0
                            ) {
                              setFieldValue("contract.resourceTerms", [
                                {
                                  resourceType: "DRIVER" as any,
                                  quantity: 1,
                                  ratePerResource: "",
                                  rateType: RateType.DAILY,
                                },
                              ]);
                            }
                          }
                        }}
                        label={`${ProjectTabEnum.CONTRACT.label} Type`}
                        {...commonTextFieldProps.SelectProps}
                        required
                      >
                        {Object.values(ContractType).map((type) => (
                          <MenuItem
                            key={type}
                            value={type}
                            sx={{ fontSize: "0.75rem" }}
                          >
                            {type}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </Grid>
                  {(values.contract?.basicInfo?.contractType ===
                    ContractType.VEHICLE_ONLY ||
                    values.contract?.basicInfo?.contractType ===
                      ContractType.VEHICLE_INCL_LABOR) && (
                    <Grid item xs={12} md={1.5}>
                      <TextField
                        {...commonTextFieldProps}
                        fullWidth
                        label="Vehicle Count"
                        name="contract.basicInfo.vehicleCount"
                        value={values.contract?.basicInfo?.vehicleCount}
                        onChange={handleChange}
                        type="number"
                      />
                    </Grid>
                  )}
                  {(values.contract?.basicInfo?.contractType ===
                    ContractType.VEHICLE_INCL_LABOR ||
                    values.contract?.basicInfo?.contractType ===
                      ContractType.LABOR_ONLY) && (
                    <Grid item xs={12} md={1.5}>
                      <TextField
                        {...commonTextFieldProps}
                        fullWidth
                        label="WorkForce Count"
                        name="contract.basicInfo.resourceCount"
                        value={values.contract?.basicInfo?.resourceCount}
                        onChange={handleChange}
                        type="number"
                      />
                    </Grid>
                  )}
                </Grid>

                {/* Contract Terms Section */}
                {/* Vehicle + Driver: Show both vehicle and resourceTerms sections */}
                {values.contract?.basicInfo?.contractType ===
                  ContractType.VEHICLE_INCL_LABOR && (
                  <Grid container item xs={12} spacing={1}>
                    {/* Vehicle Terms Section */}
                    <Grid item xs={12}>
                      <Typography
                        variant="subtitle2"
                        gutterBottom
                        sx={{ fontSize: "0.75rem", fontWeight: 500 }}
                      >
                        Vehicle Terms
                      </Typography>
                    </Grid>
                    <Grid item xs={12}>
                      {renderVehicleTermsWrapper(
                        values.contract,
                        setFieldValue,
                        handleChange,
                        touched,
                        errors
                      )}
                    </Grid>

                    {/* Resource Terms Section */}
                    <Grid item xs={12}>
                      <Typography
                        variant="subtitle2"
                        gutterBottom
                        sx={{ fontSize: "0.8rem", fontWeight: 500, mb: 2 }}
                      >
                        WorkForce Terms
                      </Typography>
                    </Grid>
                    <Grid item xs={12}>
                      <FieldArray name="contract.resourceTerms">
                        {({ push, remove }) => (
                          <Grid container item xs={12} spacing={1}>
                            {values.contract?.resourceTerms?.map(
                              (term, index) => (
                                <Grid
                                  container
                                  item
                                  xs={12}
                                  spacing={1}
                                  key={index}
                                >
                                  <Grid item xs={12} md={2}>
                                    <Autocomplete
                                      options={Object.values(ResourceType)}
                                      getOptionLabel={(option) =>
                                        formatLaborType(option)
                                      }
                                      renderInput={(params) => (
                                        <TextField
                                          {...params}
                                          label="WorkForce Type"
                                          {...commonTextFieldProps}
                                          placeholder="Select or type custom..."
                                          error={
                                            getFieldError(
                                              `contract.resourceTerms.${index}.resourceType`,
                                              touched,
                                              errors
                                            ).hasError
                                          }
                                          helperText={
                                            getFieldError(
                                              `contract.resourceTerms.${index}.resourceType`,
                                              touched,
                                              errors
                                            ).errorMessage
                                          }
                                        />
                                      )}
                                      value={term.resourceType}
                                      onChange={(e, value) =>
                                        setFieldValue(
                                          `contract.resourceTerms.${index}.resourceType`,
                                          value
                                        )
                                      }
                                      freeSolo
                                      selectOnFocus
                                      renderOption={(props, option) => (
                                        <li
                                          {...props}
                                          style={{ fontSize: "0.75rem" }}
                                        >
                                          {formatLaborType(option)}
                                        </li>
                                      )}
                                      isOptionEqualToValue={(option, value) =>
                                        option === value
                                      }
                                    />
                                  </Grid>

                                  <Grid item xs={12} md={1}>
                                    <TextField
                                      {...commonTextFieldProps}
                                      fullWidth
                                      label="Quantity"
                                      type="number"
                                      name={`contract.resourceTerms.${index}.quantity`}
                                      value={term.quantity}
                                      onChange={handleChange}
                                      error={
                                        getFieldError(
                                          `contract.resourceTerms.${index}.quantity`,
                                          touched,
                                          errors
                                        ).hasError
                                      }
                                      helperText={
                                        getFieldError(
                                          `contract.resourceTerms.${index}.quantity`,
                                          touched,
                                          errors
                                        ).errorMessage
                                      }
                                    />
                                  </Grid>
                                  <Grid item xs={12} md={1.5}>
                                    <FormControl
                                      fullWidth
                                      {...commonTextFieldProps}
                                    >
                                      <InputLabel sx={{ fontSize: "0.75rem" }}>
                                        Rate Type
                                      </InputLabel>
                                      <Select
                                        name={`contract.resourceTerms.${index}.rateType`}
                                        value={term.rateType}
                                        onChange={handleChange}
                                        label="Rate Type"
                                        {...commonTextFieldProps.SelectProps}
                                      >
                                        {Object.values(RateType).map((type) => (
                                          <MenuItem
                                            key={type}
                                            value={type}
                                            sx={{ fontSize: "0.75rem" }}
                                          >
                                            {type}
                                          </MenuItem>
                                        ))}
                                      </Select>
                                    </FormControl>
                                  </Grid>
                                  <Grid item xs={12} md={2}>
                                    <TextField
                                      {...commonTextFieldProps}
                                      fullWidth
                                      label="Rate per WorkForce"
                                      type="number"
                                      name={`contract.resourceTerms.${index}.ratePerResource`}
                                      value={term.ratePerResource}
                                      onChange={handleChange}
                                      error={
                                        getFieldError(
                                          `contract.resourceTerms.${index}.ratePerResource`,
                                          touched,
                                          errors
                                        ).hasError
                                      }
                                      helperText={
                                        getFieldError(
                                          `contract.resourceTerms.${index}.ratePerResource`,
                                          touched,
                                          errors
                                        ).errorMessage
                                      }
                                      InputProps={{
                                        startAdornment: (
                                          <Typography
                                            sx={{
                                              fontSize: "0.75rem",
                                              mr: 1,
                                            }}
                                          >
                                            ₹
                                          </Typography>
                                        ),
                                      }}
                                      InputLabelProps={{
                                        shrink: true,
                                      }}
                                    />
                                  </Grid>
                                  <Grid item xs={2}>
                                    <IconButton
                                      {...smallButtonProp}
                                      color="error"
                                      onClick={() => remove(index)}
                                    >
                                      <Delete />
                                    </IconButton>
                                  </Grid>
                                </Grid>
                              )
                            )}
                            <Grid item xs={12}>
                              <Button
                                {...smallButtonProp}
                                variant="outlined"
                                onClick={() =>
                                  push({
                                    resourceType: "DRIVER" as any,
                                    quantity: 1,
                                    ratePerResource: "",
                                    rateType: RateType.DAILY,
                                  })
                                }
                              >
                                Add WorkForce
                              </Button>
                            </Grid>
                          </Grid>
                        )}
                      </FieldArray>
                    </Grid>
                  </Grid>
                )}

                <Grid container item xs={12} spacing={1}>
                  {/* Vehicle Only: Show only vehicle terms */}
                  {values.contract?.basicInfo?.contractType ===
                    ContractType.VEHICLE_ONLY && (
                    <>
                      <Grid item xs={12}>
                        <Typography
                          variant="subtitle2"
                          gutterBottom
                          sx={{ fontSize: "0.75rem", fontWeight: 500 }}
                        >
                          Vehicle Terms
                        </Typography>
                      </Grid>
                      <Grid item xs={12}>
                        {renderVehicleTermsWrapper(
                          values.contract,
                          setFieldValue,
                          handleChange,
                          touched,
                          errors
                        )}
                      </Grid>
                    </>
                  )}

                  {/* Resource Only: Show only resourceTerms terms */}
                  {values.contract?.basicInfo?.contractType ===
                    ContractType.LABOR_ONLY && (
                    <Grid item xs={12}>
                      <Typography
                        variant="subtitle2"
                        gutterBottom
                        sx={{ fontSize: "0.75rem", fontWeight: 500 }}
                      >
                        WorkForce Terms
                      </Typography>
                      <FieldArray name="contract.resourceTerms">
                        {({ push, remove }) => (
                          <Box>
                            {values.contract?.resourceTerms?.map(
                              (term, index) => (
                                <Box
                                  key={index}
                                  sx={{
                                    mb: 2.5,
                                  }}
                                >
                                  <Grid container spacing={1.5}>
                                    <Grid item xs={12} md={2}>
                                      <Autocomplete
                                        options={Object.values(ResourceType)}
                                        getOptionLabel={(option) =>
                                          formatLaborType(option)
                                        }
                                        renderInput={(params) => (
                                          <TextField
                                            {...params}
                                            label="WorkForce Type"
                                            {...commonTextFieldProps}
                                            placeholder="Select or type custom..."
                                            error={
                                              getFieldError(
                                                `contract.resourceTerms.${index}.resourceType`,
                                                touched,
                                                errors
                                              ).hasError
                                            }
                                            helperText={
                                              getFieldError(
                                                `contract.resourceTerms.${index}.resourceType`,
                                                touched,
                                                errors
                                              ).errorMessage
                                            }
                                          />
                                        )}
                                        value={term.resourceType}
                                        onChange={(e, value) =>
                                          setFieldValue(
                                            `contract.resourceTerms.${index}.resourceType`,
                                            value
                                          )
                                        }
                                        freeSolo
                                        selectOnFocus
                                        renderOption={(props, option) => (
                                          <li
                                            {...props}
                                            style={{ fontSize: "0.75rem" }}
                                          >
                                            {formatLaborType(option)}
                                          </li>
                                        )}
                                        isOptionEqualToValue={(option, value) =>
                                          option === value
                                        }
                                      />
                                    </Grid>

                                    <Grid item xs={12} md={1}>
                                      <TextField
                                        {...commonTextFieldProps}
                                        fullWidth
                                        label="Quantity"
                                        type="number"
                                        name={`contract.resourceTerms.${index}.quantity`}
                                        value={term.quantity}
                                        onChange={handleChange}
                                        error={
                                          getFieldError(
                                            `contract.resourceTerms.${index}.quantity`,
                                            touched,
                                            errors
                                          ).hasError
                                        }
                                        helperText={
                                          getFieldError(
                                            `contract.resourceTerms.${index}.quantity`,
                                            touched,
                                            errors
                                          ).errorMessage
                                        }
                                      />
                                    </Grid>
                                    <Grid item xs={12} md={1.5}>
                                      <FormControl
                                        fullWidth
                                        {...commonTextFieldProps}
                                      >
                                        <InputLabel
                                          sx={{ fontSize: "0.75rem" }}
                                        >
                                          Rate Type
                                        </InputLabel>
                                        <Select
                                          name={`contract.resourceTerms.${index}.rateType`}
                                          value={term.rateType}
                                          onChange={handleChange}
                                          label="Rate Type"
                                          {...commonTextFieldProps.SelectProps}
                                        >
                                          {Object.values(RateType).map(
                                            (type) => (
                                              <MenuItem
                                                key={type}
                                                value={type}
                                                sx={{ fontSize: "0.75rem" }}
                                              >
                                                {type}
                                              </MenuItem>
                                            )
                                          )}
                                        </Select>
                                      </FormControl>
                                    </Grid>
                                    <Grid item xs={12} md={2}>
                                      <TextField
                                        {...commonTextFieldProps}
                                        fullWidth
                                        label="Rate per WorkForce"
                                        type="number"
                                        name={`contract.resourceTerms.${index}.ratePerResource`}
                                        value={term.ratePerResource}
                                        onChange={handleChange}
                                        error={
                                          getFieldError(
                                            `contract.resourceTerms.${index}.ratePerResource`,
                                            touched,
                                            errors
                                          ).hasError
                                        }
                                        helperText={
                                          getFieldError(
                                            `contract.resourceTerms.${index}.ratePerResource`,
                                            touched,
                                            errors
                                          ).errorMessage
                                        }
                                        InputProps={{
                                          startAdornment: (
                                            <Typography
                                              sx={{
                                                fontSize: "0.75rem",
                                                mr: 1,
                                              }}
                                            >
                                              ₹
                                            </Typography>
                                          ),
                                        }}
                                        InputLabelProps={{
                                          shrink: true,
                                        }}
                                      />
                                    </Grid>
                                    <Grid item xs={2}>
                                      <IconButton
                                        {...smallButtonProp}
                                        color="error"
                                        onClick={() => remove(index)}
                                      >
                                        <Delete />
                                      </IconButton>
                                    </Grid>
                                  </Grid>
                                </Box>
                              )
                            )}
                            <Button
                              {...smallButtonProp}
                              variant="outlined"
                              onClick={() =>
                                push({
                                  resourceType: "DRIVER" as any,
                                  quantity: 1,
                                  ratePerResource: "",
                                  rateType: RateType.DAILY,
                                })
                              }
                            >
                              Add WorkForce
                            </Button>
                          </Box>
                        )}
                      </FieldArray>
                    </Grid>
                  )}
                </Grid>

                {/* Fuel Included Section */}
                <Grid container item xs={12} spacing={1}>
                  <Grid item xs={12} md={1.5}>
                    <FormControlLabel
                      control={
                        <Checkbox
                          name="contract.fuelConfig.fuelIncluded"
                          checked={
                            values.contract?.fuelConfig?.fuelIncluded || false
                          }
                          onChange={handleChange}
                          size="small"
                          sx={{
                            "& .MuiCheckbox-root": {
                              padding: 0,
                            },
                          }}
                        />
                      }
                      label="Fuel Included"
                      sx={{
                        fontSize: "0.75rem",
                        "& .MuiFormControlLabel-label": {
                          fontSize: "0.75rem",
                        },
                      }}
                    />
                  </Grid>
                  {values.contract?.fuelConfig?.fuelIncluded && (
                    <>
                      <Grid item xs={12} md={1.5}>
                        <TextField
                          {...commonTextFieldProps}
                          fullWidth
                          label="Fuel Rate"
                          type="number"
                          name="contract.fuelConfig.fuelRate"
                          value={values.contract?.fuelConfig?.fuelRate || ""}
                          onChange={handleChange}
                          InputProps={{
                            endAdornment: (
                              <Typography sx={{ fontSize: "0.75rem", ml: 1 }}>
                                ₹/L
                              </Typography>
                            ),
                          }}
                          InputLabelProps={{
                            shrink: true,
                          }}
                        />
                      </Grid>
                      <Grid item xs={12} md={2}>
                        <TextField
                          {...commonTextFieldProps}
                          fullWidth
                          label="Fuel City"
                          name="contract.fuelConfig.fuelCity"
                          value={values.contract?.fuelConfig?.fuelCity || ""}
                          onChange={handleChange}
                        />
                      </Grid>
                      <Grid item xs={12} md={2}>
                        <TextField
                          {...commonTextFieldProps}
                          fullWidth
                          label="Fuel Variance"
                          name="contract.fuelConfig.fuelVariance"
                          value={
                            values.contract?.fuelConfig?.fuelVariance || ""
                          }
                          onChange={handleChange}
                        />
                      </Grid>
                      <Grid item xs={12} md={2}>
                        <TextField
                          {...commonTextFieldProps}
                          fullWidth
                          label="Fuel Mileage"
                          name="contract.fuelConfig.fuelMileage"
                          value={values.contract?.fuelConfig?.fuelMileage || ""}
                          onChange={handleChange}
                        />
                      </Grid>
                    </>
                  )}
                </Grid>

                {/* Additional Terms Section */}
                <Grid item xs={12}>
                  <Typography
                    variant="h6"
                    gutterBottom
                    sx={{ fontSize: "0.75rem", fontWeight: 600 }}
                  >
                    Additional Terms
                  </Typography>

                  {/* Profit Sharing Section */}
                  <Grid item xs={12}>
                    <Grid container spacing={1.5}>
                      <Grid item xs={12} md={1.5}>
                        <FormControlLabel
                          control={
                            <Checkbox
                              name="contract.profitSharing.profitSharingEnabled"
                              checked={
                                values.contract?.profitSharing
                                  ?.profitSharingEnabled || false
                              }
                              onChange={handleChange}
                              size="small"
                              sx={{
                                "& .MuiCheckbox-root": {
                                  padding: 0,
                                },
                              }}
                            />
                          }
                          label="Profit Sharing"
                          sx={{
                            fontSize: "0.75rem",
                            "& .MuiFormControlLabel-label": {
                              fontSize: "0.75rem",
                            },
                          }}
                        />
                      </Grid>
                      {values.contract?.profitSharing?.profitSharingEnabled && (
                        <>
                          <Grid item xs={12} md={2}>
                            <FormControl fullWidth {...commonTextFieldProps}>
                              <InputLabel sx={{ fontSize: "0.75rem" }}>
                                Profit Calculation Method
                              </InputLabel>
                              <Select
                                name="contract.profitSharing.profitCalculationMethod"
                                value={
                                  values.contract?.profitSharing
                                    ?.profitCalculationMethod ||
                                  ProfitCalculationMethod.NET_PROFIT
                                }
                                onChange={(e) => handleChange(e as any)}
                                label="Profit Calculation Method"
                                {...commonTextFieldProps.SelectProps}
                              >
                                {Object.values(ProfitCalculationMethod).map(
                                  (type) => (
                                    <MenuItem
                                      key={type}
                                      value={type}
                                      sx={{ fontSize: "0.75rem" }}
                                    >
                                      {type}
                                    </MenuItem>
                                  )
                                )}
                              </Select>
                            </FormControl>
                          </Grid>
                          <Grid item xs={12} md={1.5}>
                            <TextField
                              {...commonTextFieldProps}
                              fullWidth
                              label="Profit Sharing Percentage"
                              type="number"
                              name="contract.profitSharing.profitSharingPercentage"
                              value={
                                values.contract?.profitSharing
                                  ?.profitSharingPercentage || ""
                              }
                              onChange={handleChange}
                              InputProps={{
                                endAdornment: (
                                  <Typography
                                    sx={{ fontSize: "0.75rem", ml: 1 }}
                                  >
                                    %
                                  </Typography>
                                ),
                              }}
                              InputLabelProps={{
                                shrink: true,
                              }}
                            />
                          </Grid>
                        </>
                      )}
                    </Grid>
                  </Grid>
                  <FieldArray name="contract.additionalTerms">
                    {({ push, remove }) => (
                      <Box>
                        <Grid container item xs={12} spacing={1} mb={1}>
                          {values.contract?.additionalTerms?.map(
                            (term, index) => (
                              <Grid
                                container
                                item
                                xs={12}
                                md={12}
                                key={index}
                                spacing={1.5}
                              >
                                <Grid item xs={12} md={2}>
                                  <Autocomplete
                                    options={Object.values(AdditionalType)}
                                    getOptionLabel={(option) =>
                                      option as string
                                    }
                                    renderInput={(params) => (
                                      <TextField
                                        {...params}
                                        label="Type"
                                        {...commonTextFieldProps}
                                        placeholder="Select or type custom..."
                                        error={
                                          getFieldError(
                                            `contract.additionalTerms.${index}.type`,
                                            touched,
                                            errors
                                          ).hasError
                                        }
                                        helperText={
                                          getFieldError(
                                            `contract.additionalTerms.${index}.type`,
                                            touched,
                                            errors
                                          ).errorMessage
                                        }
                                      />
                                    )}
                                    value={term.type}
                                    onChange={(e, value) =>
                                      setFieldValue(
                                        `contract.additionalTerms.${index}.type`,
                                        value
                                      )
                                    }
                                    freeSolo
                                    selectOnFocus
                                    renderOption={(props, option) => (
                                      <li
                                        {...props}
                                        style={{ fontSize: "0.75rem" }}
                                      >
                                        {option}
                                      </li>
                                    )}
                                    isOptionEqualToValue={(option, value) =>
                                      option === value
                                    }
                                  />
                                </Grid>
                                <Grid item xs={12} md={2}>
                                  <TextField
                                    {...commonTextFieldProps}
                                    fullWidth
                                    label="Amount"
                                    name={`contract.additionalTerms.${index}.amount`}
                                    value={term.amount || ""}
                                    onChange={handleChange}
                                    error={
                                      getFieldError(
                                        `contract.additionalTerms.${index}.amount`,
                                        touched,
                                        errors
                                      ).hasError
                                    }
                                    helperText={
                                      getFieldError(
                                        `contract.additionalTerms.${index}.amount`,
                                        touched,
                                        errors
                                      ).errorMessage
                                    }
                                    InputProps={{
                                      startAdornment: (
                                        <Typography
                                          sx={{ fontSize: "0.75rem", mr: 1 }}
                                        >
                                          ₹
                                        </Typography>
                                      ),
                                    }}
                                    InputLabelProps={{
                                      shrink: true,
                                    }}
                                  />
                                </Grid>
                                <Grid item xs={12} md={2}>
                                  <TextField
                                    {...commonTextFieldProps}
                                    fullWidth
                                    label="Remarks"
                                    name={`contract.additionalTerms.${index}.remarks`}
                                    value={term.remarks || ""}
                                    onChange={handleChange}
                                  />
                                </Grid>
                                <Grid item xs={2}>
                                  <IconButton
                                    {...smallButtonProp}
                                    color="error"
                                    onClick={() => remove(index)}
                                  >
                                    <Delete />
                                  </IconButton>
                                </Grid>
                              </Grid>
                            )
                          )}
                        </Grid>
                        <Button
                          variant="outlined"
                          onClick={() =>
                            push({ type: "", amount: 0, remarks: "" })
                          }
                          {...smallButtonProp}
                        >
                          Add Additional Term
                        </Button>
                      </Box>
                    )}
                  </FieldArray>
                </Grid>

                {/* Division Section */}
                <Grid item xs={12}>
                  <Typography
                    variant="h6"
                    sx={{ fontSize: "0.9rem", fontWeight: 500 }}
                    color="primary.dark"
                  >
                    Divisions
                  </Typography>
                </Grid>
                <Grid item xs={12} md={12}>
                  {/* switch to toggle on division basis */}
                  <FormControlLabel
                    control={
                      <Checkbox
                        name="onDivisionBasis"
                        checked={values.onDivisionBasis || false}
                        onChange={(e) => {
                          setFieldValue("onDivisionBasis", e.target.checked);
                          if (e.target.checked) {
                            setFieldValue("divisions", [
                              { name: "", description: "" },
                            ]);
                          } else {
                            setFieldValue("divisions", []);
                          }
                        }}
                        size="small"
                        sx={{
                          "& .MuiCheckbox-root": {
                            padding: 0,
                          },
                        }}
                      />
                    }
                    label="On Division / Zone Basis"
                    sx={{
                      fontSize: "0.75rem",
                      "& .MuiFormControlLabel-label": {
                        fontSize: "0.75rem",
                      },
                    }}
                  />
                </Grid>
                {values.onDivisionBasis && (
                  <Grid item xs={12}>
                    <FieldArray name="divisions">
                      {({ push, remove }) => (
                        <>
                          <Grid container item xs={12} spacing={1} mb={1}>
                            {values.divisions?.map((division, index) => (
                              <Grid
                                container
                                item
                                xs={12}
                                md={12}
                                key={index}
                                spacing={1.5}
                              >
                                <Grid item xs={12} md={2}>
                                  <TextField
                                    fullWidth
                                    label="Name"
                                    name={`divisions.${index}.name`}
                                    value={division.name}
                                    onChange={handleChange}
                                    error={
                                      getFieldError(
                                        `divisions.${index}.name`,
                                        touched,
                                        errors
                                      ).hasError
                                    }
                                    helperText={
                                      getFieldError(
                                        `divisions.${index}.name`,
                                        touched,
                                        errors
                                      ).errorMessage
                                    }
                                    {...commonTextFieldProps}
                                  />
                                </Grid>
                                <Grid item xs={12} md={4}>
                                  <TextField
                                    fullWidth
                                    label="Description"
                                    name={`divisions.${index}.description`}
                                    value={division.description}
                                    onChange={handleChange}
                                    error={
                                      getFieldError(
                                        `divisions.${index}.description`,
                                        touched,
                                        errors
                                      ).hasError
                                    }
                                    helperText={
                                      getFieldError(
                                        `divisions.${index}.description`,
                                        touched,
                                        errors
                                      ).errorMessage
                                    }
                                    {...commonTextFieldProps}
                                  />
                                </Grid>
                                {values.divisions &&
                                  values.divisions.length > 1 && (
                                    <Grid item xs={2}>
                                      <IconButton
                                        {...smallButtonProp}
                                        color="error"
                                        onClick={() => remove(index)}
                                      >
                                        <Delete />
                                      </IconButton>
                                    </Grid>
                                  )}
                              </Grid>
                            ))}
                          </Grid>
                          <Button
                            variant="outlined"
                            onClick={() => push({ name: "", description: "" })}
                            {...smallButtonProp}
                          >
                            Add New Division
                          </Button>
                        </>
                      )}
                    </FieldArray>
                  </Grid>
                )}

                <Grid item xs={12}>
                  <DocumentUploadSection
                    title={`Upload ${ProjectTabEnum.CONTRACT.label}`}
                    selectedFiles={selectedFiles}
                    uploadedFiles={values.documents || []}
                    onFileSelect={(files: File[]) =>
                      setSelectedFiles((prev: File[]) => [...prev, ...files])
                    }
                    onFileRemove={(index: number) =>
                      setSelectedFiles((prev: File[]) =>
                        prev.filter((_, i) => i !== index)
                      )
                    }
                    onPreview={(url) => {
                      window.open(url, "_blank");
                    }}
                    isLoading={false}
                    uploadedTitle={`Uploaded ${ProjectTabEnum.CONTRACT.label} Documents`}
                  />
                </Grid>

                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    multiline
                    rows={2}
                    name="description"
                    label="Additional Notes"
                    value={values.description}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    error={touched.description && !!errors.description}
                    helperText={touched.description && errors.description}
                    {...commonTextFieldProps}
                  />
                </Grid>
                <Grid
                  item
                  xs={12}
                  sx={{ display: "flex", justifyContent: "flex-end", gap: 2 }}
                >
                  <Button
                    onClick={() => navigate(-1)}
                    disabled={isSubmitting}
                    variant="outlined"
                    {...commonButtonProps}
                    color="error"
                  >
                    Cancel
                  </Button>
                  <Button
                    type="submit"
                    variant="contained"
                    disabled={isSubmitting}
                    {...commonButtonProps}
                  >
                    {isSubmitting
                      ? "Saving..."
                      : isEditMode
                      ? "Update"
                      : "Create"}
                  </Button>
                </Grid>
              </Grid>
            </Form>
          )}
        </Formik>
      </Paper>
    </Container>
  );
};

export const renderFixedBillingItems = (
  index: number,
  values: ContractFormData,
  handleChange: (e: React.ChangeEvent<any>) => void,
  removeItem: (index: number) => void,
  setFieldValue: (field: string, value: any) => void
) => {
  return (
    <></>
    // <Grid container spacing={1.5}>
    //   <Grid item xs={12} md={2}>
    //     <FormControl fullWidth {...commonTextFieldProps}>
    //       <InputLabel sx={{ fontSize: "0.75rem" }}>Vehicle Category</InputLabel>
    //       <Select
    //         name={`fixedBillingTerms.fixedBillingTermItems.${index}.vehicleCategory`}
    //         value={
    //           values.fixedBillingTerms?.fixedBillingTermItems?.[index]
    //             ?.vehicleCategory || ""
    //         }
    //         onChange={(e) => handleChange(e as any)}
    //         label={`Vehicle Category`}
    //         {...commonTextFieldProps.SelectProps}
    //       >
    //         {Object.values(VehicleCategory).map((type: VehicleCategory) => (
    //           <MenuItem key={type} value={type} sx={{ fontSize: "0.75rem" }}>
    //             {type}
    //           </MenuItem>
    //         ))}
    //       </Select>
    //     </FormControl>
    //   </Grid>
    //   <Grid item xs={12} md={2}>
    //     <FormControl fullWidth {...commonTextFieldProps}>
    //       <InputLabel sx={{ fontSize: "0.75rem" }}>Vehicle Make</InputLabel>
    //       <Select
    //         name={`fixedBillingTerms.fixedBillingTermItems.${index}.vehicleMake`}
    //         value={
    //           values.fixedBillingTerms?.fixedBillingTermItems?.[index]
    //             ?.vehicleMake || ""
    //         }
    //         onChange={(e) => handleChange(e as any)}
    //         label={`Vehicle Make`}
    //         {...commonTextFieldProps.SelectProps}
    //       >
    //         {getVehicleMakes(
    //           values.fixedBillingTerms?.fixedBillingTermItems?.[index]
    //             ?.vehicleCategory as VehicleCategory
    //         )?.map((type: string) => (
    //           <MenuItem key={type} value={type} sx={{ fontSize: "0.75rem" }}>
    //             {type}
    //           </MenuItem>
    //         ))}
    //       </Select>
    //     </FormControl>
    //   </Grid>
    //   <Grid item xs={12} md={1.5}>
    //     <FormControl fullWidth {...commonTextFieldProps}>
    //       <InputLabel sx={{ fontSize: "0.75rem" }}>Vehicle Model</InputLabel>
    //       <Select
    //         name={`fixedBillingTerms.fixedBillingTermItems.${index}.vehicleModel`}
    //         value={
    //           values.fixedBillingTerms?.fixedBillingTermItems?.[index]
    //             ?.vehicleModel || ""
    //         }
    //         onChange={(e) => handleChange(e as any)}
    //         label={`Vehicle Model`}
    //         {...commonTextFieldProps.SelectProps}
    //       >
    //         {getVehicleModels(
    //           values.fixedBillingTerms?.fixedBillingTermItems?.[index]
    //             ?.vehicleCategory as VehicleCategory
    //         )?.map((type: string) => (
    //           <MenuItem key={type} value={type} sx={{ fontSize: "0.75rem" }}>
    //             {type}
    //           </MenuItem>
    //         ))}
    //       </Select>
    //     </FormControl>
    //   </Grid>
    //   {/* <Grid item xs={12} md={2}>
    //     <TextField
    //       {...commonTextFieldProps}
    //       fullWidth
    //       label="Pricing Type"
    //       name={`fixedBillingTerms.fixedBillingTermItems.${index}.pricingType`}
    //       value={values.fixedBillingTerms?.fixedBillingTermItems?.[index]?.pricingType || ""}
    //       onChange={handleChange}
    //     />
    //   </Grid> */}
    //   <Grid item xs={12} md={2}>
    //     <TextField
    //       {...commonTextFieldProps}
    //       fullWidth
    //       label="Amount"
    //       name={`fixedBillingTerms.fixedBillingTermItems.${index}.pricingValue`}
    //       value={
    //         values.fixedBillingTerms?.fixedBillingTermItems?.[index]
    //           ?.pricingValue &&
    //         values.fixedBillingTerms?.fixedBillingTermItems?.[index]
    //           ?.pricingValue > 0
    //           ? Number(
    //               values.fixedBillingTerms?.fixedBillingTermItems?.[index]
    //                 ?.pricingValue
    //             ).toLocaleString("en-IN")
    //           : ""
    //       }
    //       InputProps={{
    //         startAdornment: (
    //           <Typography sx={{ fontSize: "0.75rem", mr: 1 }}>₹</Typography>
    //         ),
    //       }}
    //       onChange={(e) => {
    //         const value = e.target.value.replace(/,/g, "");
    //         handleChange(e);
    //         if (value && !isNaN(Number(value))) {
    //           setFieldValue(
    //             `fixedBillingTerms.fixedBillingTermItems.${index}.pricingValue`,
    //             value
    //           );
    //         }
    //       }}
    //     />
    //   </Grid>
    //   {values.fixedBillingTerms?.fixedBillingTermItems &&
    //     values.fixedBillingTerms?.fixedBillingTermItems?.length > 1 && (
    //       <Grid item xs={12} md={2}>
    //         <IconButton
    //           {...smallButtonProp}
    //           color="error"
    //           onClick={() => removeItem(index)}
    //         >
    //           <Delete />
    //         </IconButton>
    //       </Grid>
    //     )}
    // </Grid>
  );
};

// Helper function to render vehicle terms
export const renderVehicleTerms = (
  values: ContractFormData,
  setFieldValue: (field: string, value: any) => void,
  handleChange: (e: React.ChangeEvent<any>) => void,
  touched: any,
  errors: any
) => {
  const handleCategoryChange = (e: SelectChangeEvent<Category>) => {
    handleChange(e as any);
    if (e.target.value === Category.FIXED_BILLING) {
      setFieldValue("fixedBillingTerms.fixedBillingTermItems", [
        {
          vehicleCategory: VehicleCategory.AMBULANCE,
          vehicleMake: "Force",
          vehicleModel: "ALSA",
          pricingType: PricingType.FIXED,
          pricingValue: "",
        },
      ]);
    }
  };
  return (
    <Box>
      <Box>
        <Grid container spacing={1.5}>
          {values.basicInfo.category !== Category.FIXED_BILLING && (
            <Grid item xs={12} md={2}>
              <FormControl fullWidth {...commonTextFieldProps}>
                <InputLabel sx={{ fontSize: "0.75rem" }}>
                  Vehicle {ProjectTabEnum.CONTRACT.label} Type
                </InputLabel>
                <Select
                  name="basicInfo.category"
                  value={values.basicInfo.category}
                  onChange={handleCategoryChange}
                  label={`Vehicle ${ProjectTabEnum.CONTRACT.label} Type`}
                  {...commonTextFieldProps.SelectProps}
                >
                  {getAvailableContractTypes(values.basicInfo.contractType).map(
                    (type) => (
                      <MenuItem
                        key={type}
                        value={type}
                        sx={{ fontSize: "0.75rem" }}
                      >
                        {type}
                      </MenuItem>
                    )
                  )}
                </Select>
              </FormControl>
            </Grid>
          )}

          {values.basicInfo.category === Category.FIXED && (
            <Grid container item md={10} spacing={1.5}>
              <Grid item xs={12} md={2}>
                <FormControl fullWidth {...commonTextFieldProps}>
                  <InputLabel sx={{ fontSize: "0.75rem" }}>
                    Frequency
                  </InputLabel>
                  <Select
                    name="fixedTerms.billingCycle"
                    value={
                      values.fixedTerms?.billingCycle || BillingCycle.MONTHLY
                    }
                    onChange={(e) => handleChange(e as any)}
                    label="Billing Cycle"
                    {...commonTextFieldProps.SelectProps}
                  >
                    {Object.values(BillingCycle).map((type) => (
                      <MenuItem
                        key={type}
                        value={type}
                        sx={{ fontSize: "0.75rem" }}
                      >
                        {type}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} md={2}>
                <TextField
                  {...commonTextFieldProps}
                  fullWidth
                  label="Minimum Guarantee Amount"
                  type="number"
                  name="fixedTerms.minGuaranteeAmount"
                  value={values.fixedTerms?.minGuaranteeAmount || ""}
                  onChange={handleChange}
                  InputProps={{
                    startAdornment: (
                      <Typography sx={{ fontSize: "0.75rem", mr: 1 }}>
                        ₹
                      </Typography>
                    ),
                  }}
                  InputLabelProps={{
                    shrink: true,
                  }}
                />
              </Grid>
              <Grid item xs={12} md={2}>
                <TextField
                  {...commonTextFieldProps}
                  fullWidth
                  label="Distance"
                  type="number"
                  name="fixedTerms.distance"
                  value={values.fixedTerms?.distance || ""}
                  onChange={handleChange}
                  InputProps={{
                    endAdornment: (
                      <Typography sx={{ fontSize: "0.75rem", ml: 1 }}>
                        km
                      </Typography>
                    ),
                  }}
                />
              </Grid>
            </Grid>
          )}

          {values.basicInfo.category === Category.PER_KM && (
            <Grid container item md={10} spacing={1.5}>
              <Grid item xs={12} md={2}>
                <TextField
                  {...commonTextFieldProps}
                  fullWidth
                  label="KM Limit"
                  type="number"
                  name="perKmTerms.kmLimit"
                  value={values.perKmTerms?.kmLimit || ""}
                  onChange={handleChange}
                />
              </Grid>
              <Grid item xs={12} md={1.5}>
                <TextField
                  {...commonTextFieldProps}
                  fullWidth
                  label="Rate per KM"
                  type="number"
                  name="perKmTerms.ratePerKm"
                  value={values.perKmTerms?.ratePerKm || ""}
                  onChange={handleChange}
                  InputProps={{
                    startAdornment: (
                      <Typography sx={{ fontSize: "0.75rem", mr: 1 }}>
                        ₹
                      </Typography>
                    ),
                  }}
                />
              </Grid>
              <Grid item xs={12} md={1.5}>
                <TextField
                  {...commonTextFieldProps}
                  fullWidth
                  label="Over-limit Rate"
                  type="number"
                  name="perKmTerms.overlimitRate"
                  value={values.perKmTerms?.overlimitRate || ""}
                  onChange={handleChange}
                  InputProps={{
                    startAdornment: (
                      <Typography sx={{ fontSize: "0.75rem", mr: 1 }}>
                        ₹
                      </Typography>
                    ),
                  }}
                />
              </Grid>
            </Grid>
          )}

          {values.basicInfo.category === Category.PER_SEAT && (
            <Grid container item md={10} spacing={1.5}>
              <Grid item xs={12} md={1.5}>
                <TextField
                  {...commonTextFieldProps}
                  fullWidth
                  label="Seat Count"
                  type="number"
                  name="perSeatTerms.seatCount"
                  value={values.perSeatTerms?.seatCount || ""}
                  onChange={handleChange}
                />
              </Grid>
              <Grid item xs={12} md={1.5}>
                <TextField
                  {...commonTextFieldProps}
                  fullWidth
                  label="Rate per Seat"
                  type="number"
                  name="perSeatTerms.ratePerSeat"
                  value={values.perSeatTerms?.ratePerSeat || ""}
                  onChange={handleChange}
                  InputProps={{
                    startAdornment: (
                      <Typography sx={{ fontSize: "0.75rem", mr: 1 }}>
                        ₹
                      </Typography>
                    ),
                  }}
                  InputLabelProps={{
                    shrink: true,
                  }}
                />
              </Grid>
              <Grid item xs={12} md={1.5}>
                <TextField
                  {...commonTextFieldProps}
                  fullWidth
                  label="Minimum Seats Guarantee"
                  type="number"
                  name="perSeatTerms.minSeatsGuarantee"
                  value={values.perSeatTerms?.minSeatsGuarantee || ""}
                  onChange={handleChange}
                  InputProps={{
                    startAdornment: (
                      <Typography sx={{ fontSize: "0.75rem", mr: 1 }}>
                        ₹
                      </Typography>
                    ),
                  }}
                  InputLabelProps={{
                    shrink: true,
                  }}
                />
              </Grid>
            </Grid>
          )}

          {values.basicInfo.category === Category.FIXED_BILLING && (
            <Grid container item md={12} spacing={1.5}>
              <Grid item xs={12} md={2}>
                <FormControl fullWidth {...commonTextFieldProps}>
                  <InputLabel sx={{ fontSize: "0.75rem" }}>
                    Vehicle {ProjectTabEnum.CONTRACT.label} Type
                  </InputLabel>
                  <Select
                    name="basicInfo.category"
                    value={values.basicInfo.category}
                    onChange={handleCategoryChange}
                    label={`Vehicle ${ProjectTabEnum.CONTRACT.label} Type`}
                    {...commonTextFieldProps.SelectProps}
                  >
                    {getAvailableContractTypes(
                      values.basicInfo.contractType
                    ).map((type) => (
                      <MenuItem
                        key={type}
                        value={type}
                        sx={{ fontSize: "0.75rem" }}
                      >
                        {type}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} md={2}>
                <FormControl fullWidth {...commonTextFieldProps}>
                  <InputLabel sx={{ fontSize: "0.75rem" }}>
                    Frequency
                  </InputLabel>
                  <Select
                    name="fixedTerms.billingCycle"
                    value={
                      values.fixedTerms?.billingCycle || BillingCycle.MONTHLY
                    }
                    onChange={(e) => handleChange(e as any)}
                    label="Billing Cycle"
                    {...commonTextFieldProps.SelectProps}
                  >
                    {Object.values(BillingCycle).map((type) => (
                      <MenuItem
                        key={type}
                        value={type}
                        sx={{ fontSize: "0.75rem" }}
                      >
                        {type}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} md={12}>
                {/* <FieldArray name="contract.fixedBillingTerms.fixedBillingTermItems">
                  {({ push, remove }) => (
                    <Box>
                      <Typography
                        variant="subtitle2"
                        gutterBottom
                        sx={{ fontSize: "0.8rem", fontWeight: 500, mb: 2 }}
                      >
                        Vehicle List
                      </Typography>
                      {values.fixedBillingTerms?.fixedBillingTermItems?.map(
                        (item, index) => (
                          <Box key={index} sx={{ mb: 2 }}>
                            {renderFixedBillingItems(
                              index,
                              values,
                              handleChange,
                              remove,
                              setFieldValue
                            )}
                          </Box>
                        )
                      )}
                      <Button
                        {...smallButtonProp}
                        variant="outlined"
                        onClick={() =>
                          push({
                            vehicleCategory: VehicleCategory.AMBULANCE,
                            vehicleMake: "Force",
                            vehicleModel: "ALSA",
                            pricingType: PricingType.FIXED,
                            pricingValue: "",
                          })
                        }
                      >
                        Add Vehicle
                      </Button>
                    </Box>
                  )}
                </FieldArray> */}
              </Grid>
            </Grid>
          )}
        </Grid>
      </Box>
    </Box>
  );
};

export default SuperProjectForm;
